import { Log } from './logger.js';
import config from './config.js';

const _log = Log('performance.js');

/**
 * Performance monitoring utilities for tracking render performance
 */
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.renderTimes = [];
    this.asyncOperations = [];
    this.slowRenderThreshold = 16; // 16ms = 60fps target
    this.enabled = config.ENABLE_PERFORMANCE_MONITORING;
    
    if (this.enabled) {
      _log.green('🚀 Performance monitoring enabled');
    }
  }
  
  /**
   * Time a function execution and log if it's slow
   * @param {string} componentName - Name of the component being measured
   * @param {Function} renderFn - Function to time
   * @param {string} operation - Type of operation (render, update, etc)
   * @returns {any} Result of the function
   */
  timeRender(componentName, renderFn, operation = 'render') {
    if (!this.enabled) {
      return renderFn();
    }
    
    const startTime = performance.now();
    const result = renderFn();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Store metrics
    this._recordMetric(componentName, operation, duration);
    
    // Log slow renders
    if (duration > this.slowRenderThreshold) {
      _log.yellow(`⚠️ Slow ${operation} detected:`, {
        component: componentName,
        duration: `${duration.toFixed(2)}ms`,
        threshold: `${this.slowRenderThreshold}ms`
      });
    } else if (config.IS_DEVELOPMENT) {
      _log.blue(`✅ ${operation} performance:`, {
        component: componentName,
        duration: `${duration.toFixed(2)}ms`
      });
    }
    
    return result;
  }
  
  /**
   * Time an async function execution
   * @param {string} componentName - Name of the component
   * @param {Function} asyncFn - Async function to time
   * @param {string} operation - Type of operation
   * @returns {Promise} Promise result
   */
  async timeAsync(componentName, asyncFn, operation = 'async-operation') {
    if (!this.enabled) {
      return await asyncFn();
    }
    
    const startTime = performance.now();
    const result = await asyncFn();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this._recordMetric(componentName, operation, duration);
    
    if (duration > this.slowRenderThreshold) {
      _log.yellow(`⚠️ Slow ${operation} detected:`, {
        component: componentName,
        duration: `${duration.toFixed(2)}ms`
      });
    }
    
    return result;
  }
  
  /**
   * Mark a performance milestone
   * @param {string} name - Name of the milestone
   */
  mark(name) {
    if (!this.enabled) return;
    
    performance.mark(name);
  }
  
  /**
   * Measure time between two marks
   * @param {string} name - Name of the measurement
   * @param {string} startMark - Start mark name
   * @param {string} endMark - End mark name
   */
  measure(name, startMark, endMark) {
    if (!this.enabled) return;
    
    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name, 'measure')[0];
      
      if (measure.duration > this.slowRenderThreshold) {
        _log.yellow(`⚠️ Slow measurement '${name}':`, {
          duration: `${measure.duration.toFixed(2)}ms`,
          start: startMark,
          end: endMark
        });
      }
    } catch (error) {
      _log.red('Performance measurement failed:', error.message);
    }
  }
  
  /**
   * Record a metric
   * @private
   */
  _recordMetric(component, operation, duration) {
    const key = `${component}.${operation}`;
    
    if (!this.metrics.has(key)) {
      this.metrics.set(key, {
        component,
        operation,
        count: 0,
        totalDuration: 0,
        minDuration: Infinity,
        maxDuration: -Infinity,
        recentMeasurements: []
      });
    }
    
    const metric = this.metrics.get(key);
    metric.count++;
    metric.totalDuration += duration;
    metric.minDuration = Math.min(metric.minDuration, duration);
    metric.maxDuration = Math.max(metric.maxDuration, duration);
    
    // Keep last 10 measurements for trend analysis
    metric.recentMeasurements.push(duration);
    if (metric.recentMeasurements.length > 10) {
      metric.recentMeasurements.shift();
    }
  }
  
  /**
   * Get performance statistics
   * @param {string} componentName - Optional component filter
   * @returns {object} Performance stats
   */
  getStats(componentName = null) {
    const stats = {
      renderTimes: this.renderTimes,
      asyncOperations: this.asyncOperations
    };
    
    for (const [key, metric] of this.metrics) {
      if (componentName && !key.startsWith(componentName)) {
        continue;
      }
      
      const avgDuration = metric.totalDuration / metric.count;
      const recentAvg = metric.recentMeasurements.reduce((sum, d) => sum + d, 0) / metric.recentMeasurements.length;
      
      stats[key] = {
        component: metric.component,
        operation: metric.operation,
        count: metric.count,
        averageDuration: parseFloat(avgDuration.toFixed(2)),
        recentAverageDuration: parseFloat(recentAvg.toFixed(2)),
        minDuration: parseFloat(metric.minDuration.toFixed(2)),
        maxDuration: parseFloat(metric.maxDuration.toFixed(2)),
        slowRenders: metric.recentMeasurements.filter(d => d > this.slowRenderThreshold).length
      };
    }
    
    return stats;
  }
  
  /**
   * Get the worst performing components
   * @param {number} limit - Maximum number of results
   * @returns {Array} Worst performers
   */
  getWorstPerformers(limit = 5) {
    const stats = this.getStats();
    
    return Object.values(stats)
      .sort((a, b) => b.recentAverageDuration - a.recentAverageDuration)
      .slice(0, limit)
      .map(stat => ({
        component: stat.component,
        operation: stat.operation,
        avgDuration: stat.recentAverageDuration,
        slowRenders: stat.slowRenders
      }));
  }
  
  /**
   * Start timing a render operation
   * @param {string} componentName - Name of the component
   */
  startRender(componentName) {
    if (!this.enabled) return;
    
    this._startTime = this._startTime || new Map();
    this._startTime.set(`render_${componentName}`, performance.now());
  }
  
  /**
   * End timing a render operation
   * @param {string} componentName - Name of the component
   */
  endRender(componentName) {
    if (!this.enabled) return;
    
    const startTime = this._startTime?.get(`render_${componentName}`);
    if (startTime) {
      const duration = performance.now() - startTime;
      this._recordMetric(componentName, 'render', duration);
      this._startTime.delete(`render_${componentName}`);
      
      // Add to renderTimes array for test compatibility
      this.renderTimes.push({
        name: componentName,
        duration,
        timestamp: Date.now()
      });
      
      if (duration > this.slowRenderThreshold) {
        _log.yellow(`⚠️ Slow render detected:`, {
          component: componentName,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    }
  }
  
  /**
   * Start timing an async operation
   * @param {string} operationName - Name of the operation
   */
  startAsyncOperation(operationName) {
    if (!this.enabled) return;
    
    this._startTime = this._startTime || new Map();
    this._startTime.set(`async_${operationName}`, performance.now());
  }
  
  /**
   * End timing an async operation
   * @param {string} operationName - Name of the operation
   */
  endAsyncOperation(operationName) {
    if (!this.enabled) return;
    
    const startTime = this._startTime?.get(`async_${operationName}`);
    if (startTime) {
      const duration = performance.now() - startTime;
      this._recordMetric(operationName, 'async', duration);
      this._startTime.delete(`async_${operationName}`);
      
      // Add to asyncOperations array for test compatibility
      this.asyncOperations.push({
        name: operationName,
        duration,
        timestamp: Date.now()
      });
    }
  }
  
  /**
   * Get slow operation threshold for testing
   */
  get slowOperationThreshold() {
    return this.slowRenderThreshold;
  }
  
  /**
   * Reset all metrics
   */
  reset() {
    this.metrics.clear();
    this.renderTimes = [];
    this.asyncOperations = [];
    this._startTime = new Map();
    _log.blue('Performance metrics reset');
  }
  
  /**
   * Log performance summary
   */
  logSummary() {
    if (!this.enabled) {
      _log.yellow('Performance monitoring is disabled');
      return;
    }
    
    const stats = this.getStats();
    const totalOperations = Object.values(stats).reduce((sum, stat) => sum + stat.count, 0);
    const worstPerformers = this.getWorstPerformers(3);
    
    _log.blue('📊 Performance Summary:', {
      totalOperations,
      componentsMonitored: new Set(Object.values(stats).map(s => s.component)).size,
      worstPerformers
    });
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Make available globally for debugging
if (config.IS_DEVELOPMENT && typeof window !== 'undefined') {
  window.performanceMonitor = performanceMonitor;
  
  // Add helper functions
  window.perfStats = () => performanceMonitor.logSummary();
  window.perfWorst = () => console.table(performanceMonitor.getWorstPerformers());
  window.perfReset = () => performanceMonitor.reset();
}

export default performanceMonitor;
